<template>
    <store-layout>
        <div>
            <div class="d-flex align-items-center justify-content-between flex-wrap button-toolbar">
                <div class="d-flex align-items-center flex-wrap button-toolbar w-75">
                    <preview-link :frontend-url="frontendUrl" :link="storeLink" class="mb-3"/>
                    <portal-target class="mb-3" name="navigation"></portal-target>
                </div>
                <button @click="toggleNav" class="btn btn-fixed-height btn-bg-white mr-0 d-lg-none mb-3 align-self-end">
                    <span
                        class="burger-icon">
                        <span></span>
                    </span>
                </button>
            </div>
            <div class="d-flex flex-column flex-lg-row">

                <!--Nav-->
                <div class="flex-shrink-1 mr-3 d-none d-lg-block">
                    <navigation-group :name="__('general.settings_basic')">
                        <navigation-item :url="$route('store.edit.basic', [storeUuid])"
                                         :name="__('general.basic_information')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_BASIC, storeUuid)"
                                         icon="fa fa-info-circle">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.legal', [storeUuid])"
                                         :name="__('store.legal_info')"
                                         v-if="showLegalTab && (Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_SALES_SETTINGS, storeUuid))"
                                         icon="fas fa-balance-scale">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.invoice', [storeUuid])"
                                         :name="__('navigation.invoice_settings')"
                                         v-if="storePlan === 'premium' && (Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_SALES_SETTINGS, storeUuid))"
                                         icon="fa fa-file-invoice">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.sale', [storeUuid])"
                                         :name="__('navigation.sales_and_deliveries')"
                                         v-if="(Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_SALES_SETTINGS, storeUuid))"
                                         icon="fa fa-hand-holding-usd">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.package_dimension_presets', [storeUuid])"
                                         :name="__('general.package_dimension_presets')"
                                         v-if="storePlan === 'premium' && (Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_SALES_SETTINGS, storeUuid))"
                                         icon="fas fa-boxes">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.reservations', [storeUuid])"
                                         :name="__('general.reservations')"
                                         v-if="storePlan === 'premium' && (Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_RESERVATION_SETTINGS, storeUuid))"
                                         icon="fa fa-business-time">
                        </navigation-item>

                        <navigation-item :url="$route('store.edit.opening-hours', [storeUuid])"
                                         :name="__('general.opening_hours')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_OPENING_HOURS, storeUuid)"
                                         icon="fa fa-clock">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.logo', [storeUuid])"
                                         :name="__('general.logo')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_LOGO, storeUuid)"
                                         icon="fas fa-file-image">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.images', [storeUuid])"
                                         :name="__('store.images')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_IMAGES, storeUuid)"
                                         icon="fas fa-images">
                        </navigation-item>
                        <div v-if="showStoreFrontNavigationItems">
                            <navigation-item :url="$route('store.edit.social-media', [storeUuid])"
                                             :name="__('store.social_media')"
                                             v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_SOCIAL_MEDIA, storeUuid)"
                                             icon="fas fa-share-alt-square">
                            </navigation-item>
                            <navigation-item :url="$route('store.edit.parking', [storeUuid])"
                                             :name="__('store.parking')"
                                             v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_PARKING, storeUuid)"
                                             icon="fas fa-parking">
                            </navigation-item>
                            <navigation-item :url="$route('store.edit.payment', [storeUuid])"
                                             :name="__('store.payment_methods')"
                                             v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_PAYMENT_METHODS, storeUuid)"
                                             icon="fas fa-money-bill-wave">
                            </navigation-item>
                        </div>
                        <navigation-item :url="$route('store.edit.contact', [storeUuid])"
                                         :name="__('navigation.contact')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_SALES_SETTINGS, storeUuid)"
                                         icon="fa fa-mail-bulk">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.plan', [storeUuid])"
                                         :name="__('store.plan_name')"
                                         v-if="Permission.EDIT_PLANS"
                                         icon="fas fa-medal">
                        </navigation-item>
                    </navigation-group>
                    <!------------------------------->
                    <navigation-group v-if="showStoreFrontNavigationItems" :name="__('general.brands')" class="mt-3">
                        <navigation-item :url="$route('store.edit.brands', [storeUuid])"
                                         :name="__('general.brands')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_BRANDS, storeUuid)"
                                         icon="fas fa-bookmark">
                        </navigation-item>
                    </navigation-group>
                    <!------------------------------->
                    <navigation-group :name="__('general.access.basic')" class="mt-3">
                        <navigation-item :url="$route('store.edit.users', [storeUuid])"
                                         :name="__('navigation.users')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.VIEW_USERS, storeUuid)"
                                         icon="fas fa-users">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.roles', [storeUuid])"
                                         :name="__('navigation.roles')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.VIEW_ROLES, storeUuid)"
                                         icon="fas fa-user-lock">
                        </navigation-item>
                    </navigation-group>
                </div>

                <!--Nav-Mobile-->
                <div v-show="showMenu" class="flex-shrink-1 flex-column flex-sm-row d-lg-none justify-content-between"
                     :class="{'d-flex': showMenu}">
                    <navigation-group :name="__('general.settings_basic')" class="w-100 w-sm-50 mb-3 mr-sm-3">
                        <navigation-item :url="$route('store.edit.basic', [storeUuid])"
                                         :name="__('general.basic_information')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_BASIC, storeUuid)"
                                         icon="fa fa-info-circle"
                                         @toggleNav="toggleNav">
                        </navigation-item>

                        <navigation-item :url="$route('store.edit.invoice', [storeUuid])"
                                         :name="__('navigation.invoice_settings')"
                                         v-if="storePlan === 'premium' && (Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_SALES_SETTINGS, storeUuid))"
                                         icon="fa fa-file-invoice"
                                         @toggleNav="toggleNav">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.sale', [storeUuid])"
                                         :name="__('navigation.sale_settings')"
                                         v-if="storePlan === 'premium' && (Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_SALES_SETTINGS, storeUuid))"
                                         icon="fa fa-hand-holding-usd"
                                         @toggleNav="toggleNav">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.package_dimension_presets', [storeUuid])"
                                         :name="__('general.package_dimension_presets')"
                                         v-if="storePlan === 'premium' && (Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_SALES_SETTINGS, storeUuid))"
                                         icon="fas fa-boxes"
                                         @toggleNav="toggleNav">
                        </navigation-item>

                        <navigation-item :url="$route('store.edit.opening-hours', [storeUuid])"
                                         :name="__('general.opening_hours')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_OPENING_HOURS, storeUuid)"
                                         icon="fa fa-clock" @toggleNav="toggleNav">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.logo', [storeUuid])"
                                         :name="__('general.logo')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_LOGO, storeUuid)"
                                         icon="fas fa-file-image" @toggleNav="toggleNav">
                        </navigation-item>
                        <navigation-item :url="$route('store.edit.images', [storeUuid])"
                                         :name="__('store.images')"
                                         v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_IMAGES, storeUuid)"
                                         icon="fas fa-images" @toggleNav="toggleNav">
                        </navigation-item>
                        <div v-if="showStoreFrontNavigationItems">
                            <navigation-item :url="$route('store.edit.social-media', [storeUuid])"
                                             :name="__('store.social_media')"
                                             v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_SOCIAL_MEDIA, storeUuid)"
                                             icon="fas fa-share-alt-square" @toggleNav="toggleNav">
                            </navigation-item>
                            <navigation-item :url="$route('store.edit.parking', [storeUuid])"
                                             :name="__('store.parking')"
                                             v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_PARKING, storeUuid)"
                                             icon="fas fa-parking" @toggleNav="toggleNav">
                            </navigation-item>
                            <navigation-item :url="$route('store.edit.payment', [storeUuid])"
                                             :name="__('store.payment_methods')"
                                             v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_PAYMENT_METHODS, storeUuid)"
                                             icon="fas fa-money-bill-wave" @toggleNav="toggleNav">
                            </navigation-item>
                        </div>
                        <navigation-item :url="$route('store.edit.plan', [storeUuid])"
                                         :name="__('store.plan_name')"
                                         v-if="Permission.EDIT_PLANS"
                                         icon="fas fa-medal" @toggleNav="toggleNav">
                        </navigation-item>
                    </navigation-group>
                    <div class="d-flex flex-column w-100 w-sm-50">
                        <!------------------------------->
                        <navigation-group v-if="showStoreFrontNavigationItems" :name="__('general.brands')"
                                          class="h-100 h-sm-50 mb-3">
                            <navigation-item :url="$route('store.edit.brands', [storeUuid])"
                                             :name="__('general.brands')"
                                             v-if="Permission.EDIT_ALL_STORES || can(StorePermission.EDIT_BRANDS, storeUuid)"
                                             icon="fas fa-bookmark" @toggleNav="toggleNav">
                            </navigation-item>
                        </navigation-group>
                        <!------------------------------->
                        <navigation-group :name="__('general.access.basic')" class="h-100 h-sm-50 mb-3">
                            <navigation-item :url="$route('store.edit.users', [storeUuid])"
                                             :name="__('navigation.users')"
                                             v-if="Permission.EDIT_ALL_STORES || can(StorePermission.VIEW_USERS, storeUuid)"
                                             icon="fas fa-users" @toggleNav="toggleNav">
                            </navigation-item>
                            <navigation-item :url="$route('store.edit.roles', [storeUuid])"
                                             :name="__('navigation.roles')"
                                             v-if="Permission.EDIT_ALL_STORES || can(StorePermission.VIEW_ROLES, storeUuid)"
                                             icon="fas fa-user-lock" @toggleNav="toggleNav">
                            </navigation-item>
                        </navigation-group>
                    </div>
                </div>
                <div class="flex-grow-1">
                    <div class="card">
                        <div class="card-body">
                            <slot></slot>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </store-layout>
</template>

<script>
import {PermissionsMixin} from '@js/permissions'
import NavigationGroup from './NavigationGroup.vue'
import NavigationItem from './NavigationItem.vue'
import StoreLayout from '@layouts/StoreLayout.vue'
import PreviewLink from '@components/Store/PreviewLink.vue'

export default {
    mixins: [PermissionsMixin],

    components: {
        PreviewLink,
        StoreLayout,
        NavigationGroup,
        NavigationItem,
    },

    props: {
        store: Object,
        storeUuid: String,
        storePlan: String,
        appLocale: String,
        integrations: {
            types: [Array, Object],
        },
        frontendUrl: String,
        storeLink: String,
        invoiceSettings: Boolean,
    },

    data() {
        return {
            showMenu: false,
        }
    },

    beforeMount() {
        this.requestStorePermissions(this.storeUuid)
    },

    methods: {
        toggleNav() {
            this.showMenu = !this.showMenu
        },
    },

    computed: {
        showStoreFrontNavigationItems() {
            return ['de', 'at', 'fr'].includes(this.appLocale)
        },

        storeCountry() {
            return this.store.attributes.address.country;
        },

        showLegalTab() {
            return ['de', 'at'].includes(this.storeCountry.toLowerCase())
        },
    },
}
</script>
